import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import type { IconData } from './icon-data.js';
import { Icon } from './Icon.js';
import { iconExpand } from './generated/icons.js';
import { cx } from './util.js';

export interface MenuItem {
  key: string;
  label: string;
  icon?: IconData;
  destructive?: boolean;
  disabled?: boolean;
  onSelect: () => void;
}

export interface MenuProps {
  items: MenuItem[];
  /** The trigger. It receives the props that wire it to the menu. */
  trigger: (props: {
    ref: React.RefObject<HTMLButtonElement>;
    onClick: () => void;
    'aria-haspopup': 'menu';
    'aria-expanded': boolean;
    'aria-controls': string | undefined;
  }) => ReactNode;
  label: string;
}

/** A menu: arrow keys move, Enter or Space picks, Escape closes and returns focus. */
export function Menu({ items, trigger, label }: MenuProps) {
  const [open, setOpen] = useState(false);
  const id = useId();
  const button = useRef<HTMLButtonElement>(null);
  const list = useRef<HTMLUListElement>(null);
  const wrap = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    list.current?.querySelector<HTMLElement>('[role="menuitem"]:not(:disabled)')?.focus();
    const away = (event: MouseEvent) => {
      if (wrap.current !== null && !wrap.current.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', away);
    return () => document.removeEventListener('mousedown', away);
  }, [open]);
  const close = () => {
    setOpen(false);
    button.current?.focus();
  };
  return (
    <div className="k-menu" ref={wrap}>
      {trigger({
        ref: button,
        onClick: () => setOpen((o) => !o),
        'aria-haspopup': 'menu',
        'aria-expanded': open,
        'aria-controls': open ? id : undefined,
      })}
      {open ? (
        <ul
          ref={list}
          id={id}
          role="menu"
          aria-label={label}
          className="k-menu__list"
          onKeyDown={(event) => {
            const els = Array.from(
              list.current?.querySelectorAll<HTMLElement>('[role="menuitem"]:not(:disabled)') ?? [],
            );
            const index = els.findIndex((el) => el === document.activeElement);
            if (event.key === 'Escape') {
              event.preventDefault();
              close();
            } else if (event.key === 'ArrowDown') {
              event.preventDefault();
              els[(index + 1) % els.length]?.focus();
            } else if (event.key === 'ArrowUp') {
              event.preventDefault();
              els[(index - 1 + els.length) % els.length]?.focus();
            } else if (event.key === 'Home') {
              event.preventDefault();
              els[0]?.focus();
            } else if (event.key === 'End') {
              event.preventDefault();
              els[els.length - 1]?.focus();
            } else if (event.key === 'Tab') {
              setOpen(false);
            }
          }}
        >
          {items.map((item) => (
            <li key={item.key} role="none">
              <button
                type="button"
                role="menuitem"
                disabled={item.disabled}
                className={cx(
                  'k-menu__item k-layer k-t-label-lg',
                  item.destructive && 'k-menu__item--destructive',
                )}
                onClick={() => {
                  close();
                  item.onSelect();
                }}
              >
                {item.icon === undefined ? null : <Icon icon={item.icon} size={20} />}
                {item.label}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

export interface SplitButtonProps {
  label: string;
  icon?: IconData;
  onClick: () => void;
  menuLabel: string;
  items: MenuItem[];
  disabled?: boolean;
}

/** The main action and a menu of alternatives, for example Share on WhatsApp and Copy link. */
export function SplitButton({
  label,
  icon,
  onClick,
  menuLabel,
  items,
  disabled,
}: SplitButtonProps) {
  return (
    <div className="k-split">
      <button
        type="button"
        className="k-btn k-btn--filled k-btn--md k-layer k-t-label-lg k-split__main"
        onClick={onClick}
        disabled={disabled}
      >
        {icon === undefined ? null : <Icon icon={icon} size={20} />}
        <span className="k-btn__label">{label}</span>
      </button>
      <Menu
        label={menuLabel}
        items={items}
        trigger={(props) => (
          <button
            {...props}
            type="button"
            className="k-btn k-btn--filled k-btn--md k-layer k-split__more"
            aria-label={menuLabel}
            disabled={disabled}
          >
            <Icon icon={iconExpand} size={20} />
          </button>
        )}
      />
    </div>
  );
}
